import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {TombOfAkhenatenParsedGameData} from "../../server/data/TombOfAkhenatenParsedGameData";
import {TombOfAkhenatenParsedInitData} from "../../server/data/TombOfAkhenatenParsedInitData";
import {Elastic, Linear, Timeline, TimelineLite, TweenLite} from "gsap";
import {TombOfAkhenatenGameConfig} from "../../TombOfAkhenatenGameConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {TOAGameEvent} from "../../events/TOAGameEvent";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {TombOfAkhenatenGameMode} from "../../gameMode/TombOfAkhenatenGameMode";
import {SoundManager} from "../../sound/SoundManager";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {TOALowSymbol} from "../../symbols/TOALowSymbol";

export class StonePress implements GameRoundPresentation {
    stagger?: number;
    abortStagger?: number;

    private _gameData: TombOfAkhenatenParsedGameData;
    private _stonePresser: ScalableLayer;
    private _tl : TimelineLite;
    private _isAbort : boolean = false;

    constructor(stagger?: number, abortStagger?: number) {
        this.stagger = stagger;
        this.abortStagger = abortStagger;
    }

    public prepare(data: ParsedServerData): void {
        this._gameData = <TombOfAkhenatenParsedGameData>data;
        this._isAbort = false;
    }

    public abort(isActive: boolean, data: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData): Timeline | null {
        this._isAbort = true;
        if(this._tl?.isActive()){
            this._tl.progress(1);
            EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.ON_STONE_PRESS_ABORT));
        }else{
            const tl = this.getAnimation(data);
            tl?.progress(1);
        }
        return null;
    }

    private isFinalReSpin(): boolean {
        return  this._gameData.nextMode.indexOf("FINAL") > -1;
    }

    public getAnimation(data: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData): Timeline | null {

        // if (!(data.nextMode === TombOfAkhenatenGameMode.NORMAL || data.nextMode === TombOfAkhenatenGameMode.FREESPIN)) {
        //     EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.UPDATE_STONE_WAYS, data.activeRowsNextSpin));
        // }

        this.fixSymbolCutOffs(data);
        this._stonePresser = StageManager.getLayer(TombOfAkhenatenGameConfig.instance.LAYERS.STONE_PRESSER.name);

        let rowCount: number = this._gameData.activeRowsNextSpin;
        let stonePosY = TombOfAkhenatenGameConfig.instance.REEL_AREA_HEIGHT / rowCount;
        let tlDelayTime: number = this.isFinalReSpin() ? 0.5 : 0.75;

        if(this._tl?.isActive()){
            this._tl.pause().kill();
        }
        this._tl = new TimelineLite();
        this._tl.add(() => {
            //#275
            EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.SYM_STATE_HANDLER, this._isAbort));
            if((data as TombOfAkhenatenParsedGameData).isBigWin){
                EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.REMOVE_SYM_STATE_FIRE));
            }
        });
        if (this._gameData.extraWildPosition.length && this._gameData.extraWildPosition[0] != -1) {
            EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.PLAY_WILD_TRANSFORM, this._gameData.extraWildPosition, this._isAbort));
            //!this.isFinalReSpin() && tl.add(TweenLite.to({}, 1.5, {})); //delay to complete transform anim
        }

        if (data.activeRowsNextSpin === 3) {
            if (!(data.nextMode === TombOfAkhenatenGameMode.NORMAL || data.nextMode === TombOfAkhenatenGameMode.FREESPIN)) {
                EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.UPDATE_STONE_WAYS, data.activeRowsNextSpin));
            }
            return null;
        }

        this._tl.add(TweenLite.to(() => {
            //blank tween
        }, 0.02, {}), tlDelayTime);

        //stone press animation
        if (!(this._gameData.nextMode.indexOf(TombOfAkhenatenGameMode.RESPIN_FINAL) > -1)) {
            this._tl.add([
                () => {
                    if(!this._isAbort){
                        EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.UPDATE_MULTIPLIER, this._gameData.payoutMultiplierNextSpin, this._isAbort));
                    }
                },
                () => {
                    !this._isAbort && EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.ON_STONE_PRESS, rowCount, this._isAbort));
                },
                () => {
                    SoundManager.playStonePressSound(data.activeRows);

                    if(data.nextMode === TombOfAkhenatenGameMode.RESPIN_FIRST){
                        SoundManager.playMainGameRespinFirstBgSound();
                    }else if(data.nextMode === TombOfAkhenatenGameMode.RESPIN_SECOND){
                        SoundManager.playMainGameRespinSecondBgSound();
                    }
                },
                TweenLite.fromTo(this._stonePresser.position, TombOfAkhenatenGameConfig.instance.STONE_PRESS_DURATION, {y: 0}, {
                    delay: TombOfAkhenatenGameConfig.instance.STONE_PRESS_START_DELAY,
                    y: stonePosY,
                    ease: Elastic.easeOut.config(1, 0.5),
                    onComplete: () => {
                        if (!(data.nextMode === TombOfAkhenatenGameMode.NORMAL || data.nextMode === TombOfAkhenatenGameMode.FREESPIN)) {
                            EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.UPDATE_STONE_WAYS, data.activeRowsNextSpin));
                        }
                        TweenLite.fromTo(this._stonePresser.position, TombOfAkhenatenGameConfig.instance.STONE_LIFT_DURATION, {y: stonePosY}, {
                            delay: TombOfAkhenatenGameConfig.instance.STONE_LIFT_START_DELAY,
                            y: 0,
                            ease: Linear.easeNone
                        });
                    }
                })
            ]);
        }else{
            this._tl.add([
                () => {
                    if(!this._isAbort){
                        EventHandler.dispatchEvent(new GameEvent(TOAGameEvent.UPDATE_MULTIPLIER, this._gameData.payoutMultiplierNextSpin, this._isAbort));
                    }
                }
            ]);
        }

        return this._tl;
    }

    private fixSymbolCutOffs(data: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData): void{
        let checkNextRow: number[] = [0, 1, 5, 6, 10, 11, 15, 16, 20, 21];

        if (data.nextMode.indexOf("FIRST") > -1) {

            for (let i: number = 0; i < checkNextRow.length; i++) {
                let symbolName: string | undefined = Reels.getSymbol(checkNextRow[i], 0)?.symName;

                if (symbolName && TombOfAkhenatenGameConfig.instance.OVERLAPPING_SYMBOLS.indexOf(symbolName) > -1) {
                    let reelController = Reels.getReel(checkNextRow[i]);
                    if (reelController) {
                        const replacingSym = new TOALowSymbol("L1", checkNextRow[i]);
                        const reelSym = reelController.allSymbols.pop();
                        if (reelSym) {
                            replacingSym.position.set(reelSym.position.x, reelSym.position.y);
                            reelSym.parent?.addChild(replacingSym);
                            reelSym.visible = false;
                            reelController.removedChildOnView(reelSym)
                            reelController.allSymbols.push(replacingSym);
                        }
                    }
                }
            }
        }
    }
}

